import React, { useEffect, useState } from "react";
import { Search, MessageCircle, Mail, Sparkles } from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import { absUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Design Review", "Quote Shared", "Confirmed", "Won", "Closed"];

const waLink = (e) => {
  const num = String(e.phone || "").replace(/\D/g, "");
  if (!num) return null;
  const msg = `Hi ${e.name || "there"}, thank you for your custom silver gift enquiry with Wonder Parcel. We'd love to discuss your design idea, feasibility and next steps.`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

export default function CustomSilverEnquiries() {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setItems(null);
    try { const { data } = await adminApi.get("/custom-silver-enquiries"); setItems(data); }
    catch { setItems([]); toast.error("Could not load enquiries"); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/custom-silver-enquiries/${id}`, { status });
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch { toast.error("Could not update status"); }
  };

  const filtered = (items || []).filter((e) => {
    if (!query) return true;
    const ql = query.toLowerCase();
    return [e.name, e.phone, e.city, e.jewellery_type, e.occasion, e.budget, e.status, e.idea].some((v) => (v || "").toString().toLowerCase().includes(ql));
  });

  return (
    <div data-testid="admin-custom-silver-enquiries">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl">Custom Silver Enquiries</h1><p className="text-sm text-wp-ink-muted">{items ? `${filtered.length} enquiry(ies)` : "Loading…"}</p></div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-wp-ink-muted" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search enquiries…" className="w-56 rounded-wp-md pl-8" data-testid="silver-enquiries-search" />
        </div>
      </div>

      <div className="overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft">
        {items === null ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-wp-ink-muted" />
            <p className="mt-2 text-wp-ink-muted">No custom silver enquiries yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Concept</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Idea / Requirement</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Reach out</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`silver-enquiry-row-${e.id}`}>
                  <TableCell>
                    {e.concept_image ? (
                      <img src={absUrl(e.concept_image)} alt="concept" className="h-14 w-14 rounded-md object-cover" />
                    ) : <div className="grid h-14 w-14 place-items-center rounded-md bg-wp-cream-100 text-xs text-wp-ink-muted">—</div>}
                    {e.concept_caption && <div className="mt-1 max-w-[80px] truncate text-[10px] text-wp-ink-muted" title={e.concept_caption}>{e.concept_caption}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{e.name || "—"}</div>
                    <div className="text-xs text-wp-ink-muted">{e.phone}{e.city ? ` · ${e.city}` : ""}</div>
                    <div className="text-xs text-wp-ink-muted">{new Date(e.created_at).toLocaleDateString()}</div>
                  </TableCell>
                  <TableCell className="max-w-[280px]">
                    <div className="text-sm">{[e.jewellery_type, e.occasion].filter(Boolean).join(" · ")}</div>
                    {e.idea && <div className="mt-0.5 truncate text-xs text-wp-ink-muted" title={e.idea}>{e.idea}</div>}
                    {e.notes && <div className="mt-0.5 truncate text-xs text-wp-ink-muted" title={e.notes}>Note: {e.notes}</div>}
                  </TableCell>
                  <TableCell className="text-sm">{e.budget}</TableCell>
                  <TableCell>
                    <Select value={e.status || "New"} onValueChange={(v) => setStatus(e.id, v)}>
                      <SelectTrigger className="h-8 w-36 rounded-lg text-xs" data-testid={`silver-enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st} className="text-xs">{st}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {waLink(e) && <a href={waLink(e)} target="_blank" rel="noreferrer" className="grid h-8 w-8 place-items-center rounded-lg text-wp-success hover:bg-wp-success/10" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
                      {e.email && <a href={`mailto:${e.email}`} className="grid h-8 w-8 place-items-center rounded-lg text-wp-rose-strong hover:bg-wp-blush-100" title="Email"><Mail className="h-4 w-4" /></a>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
